import { Locale } from '@/constants/locale';
import { OANDA_LANGUAGES } from '@/constants/locale';
import { TMS_LANGUAGES } from '@/constants/locale';
import { gtmTags } from '@/constants/data-layers';

export class UtilityHelper {
    private static locale = '';

    public static async sleep(time: number) {
        return await new Promise<void>((resolve) => {
            setTimeout(() => {
                resolve();
            }, time);
        });
    }

    public static async wait(isWaitDone: CallableFunction) {
        return await new Promise<void>((resolve) => {
            const wait = setInterval(() => {
                if (isWaitDone()) {
                    clearInterval(wait);
                    resolve();
                } else if (process.env.NODE_ENV !== 'production') {
                    console.info('wait');
                }
            }, 50);
        });
    }

    /**
     * @input Locale object with codes of countries is provided from "../constants/locale.ts"
     * @modification Next property you can add to locale.ts as pair code country and value
     * @return Locale Lowercase alpha-2 country code based on page url
     */
    public static getLocale() {
        if (!UtilityHelper.locale) {
            let lang = '';

            let code = window.location.pathname.split('/')[1];
            if (this.isOandaPath()) {
                code = code.replace('eu-', '');
            }
            const isCodeCountry = code.length === 2 || code.length === 5;
            if (isCodeCountry) {
                for (const key in Locale) {
                    if (
                        Locale.hasOwnProperty(key) &&
                        (Locale as any)[key] === code
                    ) {
                        lang = (Locale as any)[key];
                        break;
                    }
                }
            } else {
                if (window.location.href.indexOf('.com') > -1) {
                    lang = Locale.EN;
                } else if (
                    window.location.href.indexOf('.pl') > -1 ||
                    window.location.href.indexOf('localhost') > -1
                ) {
                    lang = Locale.PL;
                }
            }

            UtilityHelper.locale = lang;
        }

        return UtilityHelper.locale;
    }

    public static isRouteMobile(): boolean {
        const pairs = location.search.substr(1).split('&');
        let isMobile = false;

        for (const pair in pairs) {
            if (
                pairs[pair].indexOf('mobile') > -1 &&
                pairs[pair].indexOf('true') > -1
            ) {
                isMobile = true;
            }
        }

        return isMobile;
    }

    public static isDetectedMobileDevice(): boolean {
        return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    }

    public static isOandaPath(): boolean {
        return OANDA_LANGUAGES.some((country: string) =>
            window.location.href.includes('/eu-' + country.toLowerCase())
        );
    }

    public static getBaseByLocale() {
        let langPrefix = '/';
        const language = UtilityHelper.locale.toUpperCase();
        const baseUrl = process.env.BASE_URL || '/';
        if (this.isOandaPath()) {
            langPrefix = baseUrl.replace('eu-en', 'eu-' + UtilityHelper.locale);
        } else if (TMS_LANGUAGES.indexOf(language) > -1 && language !== 'PL') {
            langPrefix += UtilityHelper.locale + baseUrl;
        } else if (language === 'PL') {
            langPrefix = baseUrl;
        }
        return langPrefix;
    }

    public static afterChatLoaded() {
        const exceptions = ['ro', 'ua', 'pl', 'en'];
        let langCode = exceptions.includes(UtilityHelper.locale)
            ? UtilityHelper.locale
            : `${UtilityHelper.locale}-${UtilityHelper.locale}`;
        langCode = langCode === 'cz-cz' ? 'cs-cs' : langCode;
        window.ConvrsChat.Settings({
            disabletooltips: true,
            offset: { horizontal: 30, vertical: 50 },
            background: '#00214a',
            botavatar:
                'https://oanda.com/eu-en/themes/custom/pattern_theme/assets/logo.svg',
            title: 'Oanda Chat',
            lang: langCode,
        });
    }

    public static initializeWebChat() {
        const script = document.createElement('script');
        script.id = 'convrs-webchat';
        script.defer = true;
        script.src =
            UtilityHelper.locale !== 'pl'
                ? 'https://webchat.conv.rs/f6ed3b7d669a8613afaa0224738bc5bab6fc9b97.js'
                : 'https://webchat.conv.rs/2cce6568c7f373b065e35e2b28125f958826f150.js';
        script.onload = UtilityHelper.afterChatLoaded;
        script.onerror = () => console.error('Failed to load webchat');
        document.body.appendChild(script);
    }

    public static addGtmContent() {
        const gtmTag = document.createElement('script');
        const gtmTagContent = `
      (function (w, d, s, l, i) {
        w[l] = w[l] || [];
        w[l].push({
          'gtm.start':
            new Date().getTime(), event: 'gtm.js'
        });
        var f = d.getElementsByTagName(s)[0],
          j = d.createElement(s), dl = l != 'dataLayer' ? '&l=' + l : '';
        j.async = true;
        j.src =
          'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
        f.parentNode.insertBefore(j, f);
      })(window, document, 'script', 'dataLayer', '${gtmTags[UtilityHelper.isOandaPath() ? 'OANDA' : this.locale.toUpperCase()]}')`;
        gtmTag.append(gtmTagContent);
        const optimizelyTag = document.createElement(`script`);
        const headElement = document.getElementsByTagName('head')[0];
        const thirdElement =
            document.getElementsByTagName('head')[0].children[2];
        if (this.isOandaPath()) {
            optimizelyTag.setAttribute(
                'src',
                'https://cdn.optimizely.com/js/23275990004.js'
            );
        } else {
            optimizelyTag.setAttribute(
                'src',
                'https://cdn.optimizely.com/js/20902314291.js'
            );
        }
        headElement.insertBefore(gtmTag, thirdElement);
        headElement.insertBefore(optimizelyTag, thirdElement);
        // one trust script others than OANDA onboarding
        if (!UtilityHelper.isOandaPath()) {
            const oneTrustTag = document.createElement('script');
            const forthElement =
                document.getElementsByTagName('head')[0].children[3];
            oneTrustTag.setAttribute(
                'src',
                'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js'
            );
            oneTrustTag.setAttribute('type', 'text/javascript');
            oneTrustTag.setAttribute('charset', 'UTF-8');
            oneTrustTag.setAttribute(
                'data-domain-script',
                'c73232c2-f7a9-4a55-ab65-36491223a34d'
            );
            headElement.insertBefore(oneTrustTag, forthElement);
        }
    }
}
